import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Truck, Clock, Phone, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { CategoryCard } from '../components/CategoryCard';
import { ProductCard } from '../components/ProductCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ed1dcb87-1c9b-44b4-b8a7-b978d9d63257/artifacts/eirwjtzy_88.jpeg";
const HERO_BG = "https://images.unsplash.com/photo-1713100380566-ca143913c853?w=1920&q=80";

export const Home = () => {
  const [categories, setCategories] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, prodRes] = await Promise.all([
          axios.get(`${API}/categories`),
          axios.get(`${API}/products`)
        ]);
        setCategories(catRes.data);
        
        // Featured: products with discount first, then random selection
        const products = prodRes.data;
        const withDiscount = products.filter(p => p.discount && p.discount > 0);
        const withoutDiscount = products.filter(p => !p.discount || p.discount === 0);
        
        // Shuffle products without discount
        const shuffled = withoutDiscount.sort(() => Math.random() - 0.5);
        
        // Combine: discounted products first, then others
        const featured = [...withDiscount, ...shuffled].slice(0, 8);
        setFeaturedProducts(featured);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    
    // Refresh every 60 seconds
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] md:min-h-[90vh] flex items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src={HERO_BG}
            alt="Bebidas geladas"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-2xl">
            <img 
              src={LOGO_URL} 
              alt="Armazém 88" 
              className="h-28 md:h-36 w-auto object-contain mb-6 animate-fade-in-up rounded-xl"
              style={{ background: 'transparent' }}
            />
            
            <h1 className="text-4xl md:text-6xl font-black text-white uppercase tracking-tight leading-tight animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              Do Armazém<br />
              <span className="text-[#FFD700]">Direto pra Você!</span>
            </h1>
            
            <p className="text-white/80 text-lg md:text-xl mt-4 max-w-lg animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              Peça pelo WhatsApp e receba rápido na sua casa ou comércio. São Benedito e região!
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mt-8 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              <Link to="/produtos">
                <Button 
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-white font-bold px-8 py-6 rounded-full text-lg"
                  data-testid="hero-shop-button"
                >
                  Ver Produtos
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              
              <a href="https://wa.me/5588988654624" target="_blank" rel="noopener noreferrer">
                <Button 
                  size="lg"
                  variant="outline"
                  className="bg-[#25D366] hover:bg-[#128C7E] border-0 text-white font-bold px-8 py-6 rounded-full text-lg"
                  data-testid="hero-whatsapp-button"
                >
                  <Phone className="mr-2 w-5 h-5" />
                  Pedir Agora
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Bar */}
      <section className="bg-primary text-white py-6">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            <div className="flex items-center gap-4">
              <div className="bg-white/10 p-3 rounded-xl">
                <Truck className="w-6 h-6" />
              </div>
              <div>
                <p className="font-bold">Delivery Rápido</p>
                <p className="text-white/70 text-sm">Entrega na sua porta</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="bg-white/10 p-3 rounded-xl">
                <Clock className="w-6 h-6" />
              </div>
              <div>
                <p className="font-bold">Horário de Atendimento</p>
                <p className="text-white/70 text-sm">Seg a Sáb, 9h às 17h</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="bg-white/10 p-3 rounded-xl">
                <CreditCard className="w-6 h-6" />
              </div>
              <div>
                <p className="font-bold">Todas as Formas</p>
                <p className="text-white/70 text-sm">PIX, Cartão, Dinheiro</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="bg-white/10 p-3 rounded-xl">
                <Phone className="w-6 h-6" />
              </div>
              <div>
                <p className="font-bold">Pedido Fácil</p>
                <p className="text-white/70 text-sm">Direto pelo WhatsApp</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl md:text-4xl font-black text-foreground tracking-tight">
              Categorias
            </h2>
            <Link 
              to="/produtos" 
              className="text-primary font-medium hover:underline flex items-center gap-1"
              data-testid="see-all-categories"
            >
              Ver todas
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="bg-muted rounded-2xl h-[200px] animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 stagger-children">
              {categories.map((category, index) => (
                <CategoryCard key={category.id} category={category} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 md:py-24 bg-muted/50">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl md:text-4xl font-black text-foreground tracking-tight">
              Mais Vendidos
            </h2>
            <Link 
              to="/produtos" 
              className="text-primary font-medium hover:underline flex items-center gap-1"
              data-testid="see-all-products"
            >
              Ver todos
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-card rounded-xl h-[300px] animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 stagger-children">
              {featuredProducts.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Brands Section */}
      <section className="py-12 md:py-16 bg-white border-y">
        <div className="container mx-auto px-4 md:px-8">
          <h2 className="text-center text-lg font-medium text-muted-foreground mb-8">
            Trabalhamos com as Principais Marcas
          </h2>
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
            {/* Brahma */}
            <img 
              src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/50/Brahma_beer_logo.svg/200px-Brahma_beer_logo.svg.png" 
              alt="Brahma"
              className="h-12 md:h-16 object-contain grayscale hover:grayscale-0 transition-all opacity-70 hover:opacity-100"
            />
            {/* Skol */}
            <img 
              src="https://upload.wikimedia.org/wikipedia/commons/thumb/4/40/Skol_2018.svg/200px-Skol_2018.svg.png" 
              alt="Skol"
              className="h-10 md:h-14 object-contain grayscale hover:grayscale-0 transition-all opacity-70 hover:opacity-100"
            />
            {/* Antarctica */}
            <img 
              src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/bc/Logo_antarctica.png/200px-Logo_antarctica.png" 
              alt="Antarctica"
              className="h-12 md:h-16 object-contain grayscale hover:grayscale-0 transition-all opacity-70 hover:opacity-100"
            />
            {/* Coca-Cola */}
            <img 
              src="https://upload.wikimedia.org/wikipedia/commons/thumb/c/ce/Coca-Cola_logo.svg/200px-Coca-Cola_logo.svg.png" 
              alt="Coca-Cola"
              className="h-10 md:h-12 object-contain grayscale hover:grayscale-0 transition-all opacity-70 hover:opacity-100"
            />
            {/* Guaraná Antarctica */}
            <img 
              src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/b8/Guaran%C3%A1_Antarctica_logo.svg/200px-Guaran%C3%A1_Antarctica_logo.svg.png" 
              alt="Guaraná Antarctica"
              className="h-10 md:h-12 object-contain grayscale hover:grayscale-0 transition-all opacity-70 hover:opacity-100"
            />
            {/* Heineken */}
            <img 
              src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/5d/Heineken_logo.svg/200px-Heineken_logo.svg.png" 
              alt="Heineken"
              className="h-8 md:h-10 object-contain grayscale hover:grayscale-0 transition-all opacity-70 hover:opacity-100"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#1A1A1A]">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
            Pronto para pedir?
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-xl mx-auto">
            Peça pelo site ou WhatsApp e receba rápido na sua casa ou comércio!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/produtos">
              <Button 
                size="lg"
                className="bg-[#FFD700] hover:bg-[#FFD700]/90 text-black font-bold px-8 py-6 rounded-full text-lg"
                data-testid="cta-shop-button"
              >
                Ver Produtos
              </Button>
            </Link>
            <a href="https://wa.me/5588988654624" target="_blank" rel="noopener noreferrer">
              <Button 
                size="lg"
                className="bg-[#25D366] hover:bg-[#128C7E] text-white font-bold px-8 py-6 rounded-full text-lg whatsapp-pulse"
                data-testid="cta-whatsapp-button"
              >
                <Phone className="mr-2 w-5 h-5" />
                (88) 98865-4624
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
